import {useCallback, useContext, useEffect, useState, type ReactNode} from "react";
import {TableTopSelectionEvents} from "@/tabletop/selection/TableTopSelectionEvents.ts";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import type {Door} from "@/dungeons/model/DungeonGeometry.ts";
import useGameMasterAnnotations from "@/tabletop/dungeon/useGameMasterAnnotations.ts";
import useMapFeatureSelection from "@/tabletop/dungeon/useMapFeatureSelection.ts";
import {GameMasterMapInteractionContext, type PlacementMode} from "./GameMasterMapInteractionHooks.ts";
import useEncounterLightActions from "./useEncounterLightActions.ts";

export default function GameMasterMapInteractionProvider({children}: {children: ReactNode}) {
    const encounter = useEncounter();
    const {dungeon, dungeonSettings} = useDungeon();
    const activeLevel = dungeon.levels.find(level => level.id === dungeonSettings.levelId);
    const mapSelection = useMapFeatureSelection();
    const [showAnnotations, setAnnotationsVisible] = useState(true);
    const [placementMode, setCurrentPlacementMode] = useState<PlacementMode>(null);
    const annotations = useGameMasterAnnotations({encounterId: encounter.id, activeLevel, mapSelection,
        onPlaced: () => setCurrentPlacementMode(null)});
    const {clear, selectDoor: selectMapDoor, selectLight: selectMapLight} = mapSelection;
    const {close: closeAnnotation} = annotations;

    const closeMapPopovers = useCallback(() => {
        closeAnnotation();
        clear();
    }, [clear, closeAnnotation]);
    const selectionEvents = useContext(TableTopSelectionEvents);
    useEffect(() => selectionEvents?.subscribe(closeMapPopovers), [selectionEvents, closeMapPopovers]);
    const setPlacementMode = (mode: PlacementMode) => {
        setCurrentPlacementMode(mode);
        if (mode === "annotation") setAnnotationsVisible(true);
        if (mode) closeMapPopovers();
    };
    const setShowAnnotations = (value: boolean) => {
        setAnnotationsVisible(value);
        if (!value) closeMapPopovers();
    };
    const selectDoor = (door: Door) => {
        if (!activeLevel) return;
        closeAnnotation();
        selectMapDoor(activeLevel.id, door.id);
    };
    const selectLight = (id: string) => {
        if (!activeLevel) return;
        closeAnnotation();
        selectMapLight(activeLevel.id, id);
    };
    const lightActions = useEncounterLightActions(activeLevel, (levelId, id) => {
        setCurrentPlacementMode(null);
        selectMapLight(levelId, id);
    });
    const addAttachedLight = (combatantId: string) => {
        setCurrentPlacementMode(null);
        lightActions.addAttachedLight(combatantId);
    };

    return <GameMasterMapInteractionContext.Provider value={{showAnnotations, setShowAnnotations,
        placementMode, setPlacementMode, selection: mapSelection.selection, annotations, closeMapPopovers,
        selectDoor, selectLight, placeLight: lightActions.placeLight, addAttachedLight,
        moveLight: lightActions.moveLight}}>
        {children}
    </GameMasterMapInteractionContext.Provider>;
}
